"use client";
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Container } from "react-bootstrap";
import useEmblaCarousel from "embla-carousel-react";
import { fetchRelatedProducts } from "@/redux/productsSlice";
import { ArrowLeft01FillIcon, ArrowRight01FillIcon } from "@/utlility/icons";
const CommonButton = dynamic(
  () => import("@/components/common-components/CustomButton"),
  { ssr: false }
);
const RelatedProducts = dynamic(
  () => import("../categories/RelatedProducts"),
  { ssr: false }
);
const TestimonialSection = dynamic(
  () => import("@/components/common-components/testimonial/TestimonialSection"),
  { ssr: false }
);
const CustomModal = dynamic(
  () => import("@/components/common-components/CustomModal"),
  { ssr: false }
);
const QuickContact = dynamic(
  () => import("../categories/QuickContact"),
  { ssr: false }
);
import Image from "next/image";
import "./index.css";
import dynamic from "next/dynamic";
import PageTitle from "@/components/common-components/page-title/PageTitle";

export default function ProductDetailsPage({ productDetails, marketArea }) {
  const [activeSlideIndex, setActiveSlideIndex] = useState(0); // Track the current active slide
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false });

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      setActiveSlideIndex(emblaApi.selectedScrollSnap());
    };
    emblaApi.on("select", onSelect);
    // initial set
    onSelect();
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);
  const dispatch = useDispatch();
  const relatedProducts = useSelector(
    (state) => state.products.relatedProducts
  );
  const subCategoryId = productDetails?.subcategory?.id;
  const images = productDetails?.uploaded_product_images || [];

  const [modalShow, setModalShow] = useState(false);

  const payload = useMemo(
    () => ({
      page: 1,
      limit: 12,
      organization_key: process.env.NEXT_PUBLIC_ORGANIZATION_KEY,
      sub_category_id: subCategoryId,
      product_id: productDetails?.id,
    }),
    [subCategoryId, productDetails?.id]
  );

  useEffect(() => {
    if (subCategoryId && productDetails?.id) {
      dispatch(fetchRelatedProducts({ payload }));
    }
  }, [dispatch, payload, subCategoryId, productDetails?.id]);

  const groupedAttributes = useMemo(() => {
    return productDetails?.selected_product_attributes?.reduce(
      (acc, attribute) => {
        const groupName = attribute.attribute_group?.attribute_group_name;
        const groupValue = attribute.attribute_group_value?.attribute_group_value;

        if (groupName && groupValue) {
          if (!acc[groupName]) {
            acc[groupName] = [];
          }
          acc[groupName].push(groupValue);
        }

        return acc;
      },
      {}
    ) || {};
  }, [productDetails?.selected_product_attributes]);

  useEffect(() => {
    setActiveSlideIndex(0);
    if (emblaApi) emblaApi.scrollTo(0, true);
  }, [productDetails, emblaApi]);

  const productSchema = {
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": productDetails?.product_name,
    "image": images.map(img => img.image_url),
    "description": productDetails?.meta_description || productDetails?.description?.replace(/<[^>]*>?/gm, ''),
    "sku": productDetails?.product_code,
    "brand": {
      "@type": "Brand",
      "name": "The Dentofacial Hub"
    },
    "offers": {
      "@type": "Offer",
      "url": `${process.env.NEXT_PUBLIC_BASE_URL}/${productDetails?.product_slug}`,
      "priceCurrency": "INR",
      "price": productDetails?.selling_price,
      "availability": "https://schema.org/InStock"
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(productSchema) }}
      />
      <PageTitle
        image={productDetails?.category?.category_banner_image}
        name={productDetails?.product_name}
      />
      <section className="mb-0">
        <Container>
          <div className="grid procut-detail-content ">
            <div
              className="g-col-12 g-col-lg-6 position-relative"
              data-aos="fade-right"
            >
              <div className="embla overflow-hidden slider-big" ref={emblaRef} style={{ cursor: 'grab' }}>
                <div className="embla__container d-flex h-100">
                  {images.map((item, i) => {
                    return (
                      <div className="embla__slide flex-shrink-0 w-100 h-100" key={item.id || i}>
                        <Image
                          src={`${item.image_url}`}
                          className="object-fit-cover w-100"
                          width={800}
                          height={600}
                          priority={i === 0}
                          alt={productDetails?.product_name || "Product Image"}
                          title={productDetails?.product_name}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Conditional Navigation Buttons */}
              {images.length > 1 && (
                <div className="position-absolute z-1 top-50 translate-middle-y productdetail-slider-navigations">
                  <div className="d-flex align-items-center justify-content-between w-100">
                    {/* Previous Button */}
                    <button className={`d-flex align-items-center justify-content-center cursor-pointer navigation-icon ${activeSlideIndex === 0 ? "invisible" : ""}`} onClick={scrollPrev}>
                      <PreviousAero size={22} color="#ffffff" />
                    </button>

                    {/* Spacer to maintain consistent positioning*/}
                    <div className="flex-grow-1"></div>

                    {/* Next Button */}
                    <button className={`d-flex align-items-center justify-content-center cursor-pointer navigation-icon ${activeSlideIndex === images.length - 1 ? "invisible" : ""}`} onClick={scrollNext}>
                      <NextAero size={22} color="#ffffff" />
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="g-col-12 g-col-lg-6" data-aos="fade-left">
              <div className="sub-content">
                <h2 className="fw-semibold overflow-hidden">
                  {productDetails?.product_name}
                </h2>
                <div className="py-3 mt-3 fw-medium price-tag">
                  <strong className="fw-semibold">
                    ₹{productDetails?.selling_price}
                  </strong>{" "}
                  (MOQ: {productDetails?.min_order_quantity})
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: productDetails?.description,
                  }}
                  className="mt-3 html-content"
                ></div>
                <div className="mt-3 product-detail-list">
                  <h4 className="fw-medium additional-info-title">Additional information</h4>
                  <ul className="list-unstyled ps-0 mb-0 mt-2 pt-1 d-flex flex-column gap-1">
                    <li className="d-flex align-items-center flex-nowrap py-1">
                      <p className="text-capitalize ps-2 ms-1 fw-medium flex-shrink-1">
                        Product Code
                      </p>
                      <h5 className="product-code-value fw-medium text-capitalize ps-1 ms-2 white-space-nowrap overflow-hidden flex-grow-1">
                        {productDetails ? productDetails?.product_code : "-"}
                      </h5>
                    </li>

                    {Object.entries(groupedAttributes).map(
                      ([groupName, values], index) => (
                        <li
                          key={index}
                          className="d-flex align-items-center flex-nowrap py-1"
                        >
                          <p className="text-capitalize ps-2 ms-1 fw-medium flex-shrink-1">
                            {groupName}:
                          </p>
                          <h5 className="fw-medium text-capitalize ps-1 ms-2 white-space-nowrap overflow-hidden flex-grow-1">
                            {values.join(", ")}
                          </h5>
                        </li>
                      )
                    )}
                  </ul>
                </div>
                <div className="d-flex align-items-center justify-content-start gap-3 section-content-space ">
                  <CommonButton
                    text="Get a Quote"
                    onClick={() => setModalShow(true)}
                  />
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>
      <RelatedProducts
        relatedProducts={relatedProducts}
        marketArea={marketArea?.name}
      />
      <TestimonialSection />
      <QuickContact categoryDetails={productDetails} />
      <CustomModal
        show={modalShow}
        onHide={() => setModalShow(false)}
        productDetails={productDetails?.id}
        image={images[0]?.image_url || null}
        alt={productDetails?.product_name || ""}
        title={productDetails?.product_name || ""}
      />
    </>
  );
}
